/**
 * Card impostazioni "Risposta automatica / Out-of-office" (per account).
 * Toggle attivazione, periodo, oggetto + messaggio, generazione AI del testo.
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Sparkles, Plane } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { autoresponderApi, type EmailAccount } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';

// ISO <-> valore per <input type="datetime-local">
function isoToLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function AutoresponderCard({ accounts }: { accounts: EmailAccount[] }) {
  const qc = useQueryClient();
  const [accountId, setAccountId] = useState<string>('');

  useEffect(() => {
    if (!accountId && accounts.length > 0) {
      setAccountId(accounts.find((a) => a.is_default)?.id ?? accounts[0]!.id);
    }
  }, [accounts, accountId]);

  const [enabled, setEnabled] = useState(false);
  const [subject, setSubject] = useState('Risposta automatica');
  const [bodyText, setBodyText] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');

  const cfgQuery = useQuery({
    queryKey: ['autoresponder', accountId],
    queryFn: () => autoresponderApi.get(accountId),
    enabled: !!accountId,
  });

  useEffect(() => {
    const c = cfgQuery.data?.autoresponder;
    if (c) {
      setEnabled(c.enabled);
      setSubject(c.subject || 'Risposta automatica');
      setBodyText(c.body_text || '');
      setStartAt(isoToLocal(c.start_at));
      setEndAt(isoToLocal(c.end_at));
    }
  }, [cfgQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      autoresponderApi.update(accountId, {
        enabled,
        subject,
        body_text: bodyText,
        start_at: localToIso(startAt),
        end_at: localToIso(endAt),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['autoresponder', accountId] });
      toast.success(enabled ? 'Risposta automatica attiva' : 'Risposta automatica salvata');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Errore salvataggio'),
  });

  const aiMutation = useMutation({
    mutationFn: () => {
      const acc = accounts.find((a) => a.id === accountId);
      return autoresponderApi.generate({
        fullName: acc?.display_name ?? undefined,
        fromDate: startAt ? new Date(startAt).toLocaleDateString('it-IT') : undefined,
        toDate: endAt ? new Date(endAt).toLocaleDateString('it-IT') : undefined,
      });
    },
    onSuccess: (r) => { setSubject(r.subject); setBodyText(r.body_text); toast.success('Testo generato'); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Generazione fallita'),
  });

  if (accounts.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Plane className="h-4 w-4" /> Risposta automatica</CardTitle>
            <CardDescription>Out-of-office: risponde in automatico a chi ti scrive (una volta per mittente).</CardDescription>
          </div>
          {accounts.length > 1 && (
            <select
              className="border rounded-md bg-background px-2 py-1 text-sm"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            >
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.email_address}</option>)}
            </select>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="cursor-pointer">Attiva risposta automatica</Label>
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </div>

        {enabled && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Dal (opzionale)</Label>
                <Input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Al (opzionale)</Label>
                <Input type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Oggetto</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Messaggio</Label>
                <Button variant="ghost" size="sm" onClick={() => aiMutation.mutate()} disabled={aiMutation.isPending}>
                  {aiMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                  Genera con AI
                </Button>
              </div>
              <Textarea rows={5} value={bodyText} onChange={(e) => setBodyText(e.target.value)}
                placeholder="Sono momentaneamente assente e risponderò al mio rientro…" />
            </div>
          </>
        )}

        <div className="flex justify-end">
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !accountId}>
            {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salva risposta automatica
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
